import { Button } from '@/components/button';
import { useTranslation } from '@/contexts/i18n-context';
import { useUpdateEncountersPreferencesPharmacy } from '@/api/update-preferred-pharmacy';
import { PharmacyItem } from '@/api/api.schemas';
import { useEncounter } from '@/contexts/encounter-context';
import { useNavigate } from '@/util/react-router-dom';
import { InfoRow, TextDivider } from '@/components/encounter/info-row';
import styles from './pharmacy-list-card.module.css';
import { formatPhoneNumber } from '@/util/helpers';
import clsx from 'clsx';
import { convertMetersToMiles } from '@/util/formats';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { PharmacyImageSvg } from '@/components/svgs/pharmacy-image';

type PrescriptionPharmacy = Pick<PharmacyItem, 'id' | 'name'> & {
  id: number;
  fullAddress: string | null;
  phone: string | null;
  hasMailOrder?: boolean | null;
  distance?: number | null;
};

type Pharmacy = PharmacyItem | PrescriptionPharmacy;

const translationBase = 'visit.pharmacySelection';
interface PharmacyListCardProps {
  pharmacy: Pharmacy;
  withoutSelectButton?: boolean;
}

export function PharmacyListCard({ pharmacy, withoutSelectButton = false }: PharmacyListCardProps) {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const navigate = useNavigate();
  const { mutate: updatePreferredPharmacy, isPending } = useUpdateEncountersPreferencesPharmacy();
  const { editMode } = useVisitNavigation();

  const onSelect = () => {
    updatePreferredPharmacy(
      {
        encounterId: encounter.id,
        data: {
          doseSpotPharmacyId: pharmacy.id,
          doseSpotPharmacyName: pharmacy.name,
        },
      },
      {
        onSuccess: () => {
          invalidateEncounter();
          if (editMode) {
            navigate(`/visit/${encounter.id}/review`, { state: { editMode } });
            return;
          }
          navigate(`/visit/${encounter.id}/health-and-lifestyle`);
        },
      },
    );
  };

  const locationDetails =
    pharmacy.distance && !pharmacy.hasMailOrder
      ? [
          formatPharmacyLocation(pharmacy),
          t(`${translationBase}.infoRows.distance`, {
            distance: convertMetersToMiles(pharmacy.distance),
          }),
        ]
      : [formatPharmacyLocation(pharmacy)];

  return (
    <div className={styles.card}>
      <div className={styles.detailsContainer}>
        <PharmacyImageSvg className={styles.pharmacyImg} />

        <div className={styles.pharmacyDetails}>
          <h1 className={clsx('body-1', styles.title)}>{pharmacy.name}</h1>
          <InfoRow
            icon="pin_drop"
            title={t(`${translationBase}.infoRows.location`)}
            details={<TextDivider details={locationDetails} />}
          />
          <InfoRow
            icon="call"
            title={t(`${translationBase}.infoRows.phone`)}
            details={<TextDivider details={[formatPhoneNumber(pharmacy.phone)]} />}
          />
          {pharmacy.hasMailOrder !== undefined &&
            (pharmacy.hasMailOrder ? (
              <InfoRow icon="local_shipping" title={t(`${translationBase}.infoRows.mailOrder`)} />
            ) : (
              <InfoRow icon="home" title={t(`${translationBase}.infoRows.retail`)} />
            ))}
        </div>
      </div>

      {!withoutSelectButton && (
        <Button
          onClick={onSelect}
          variant="outlined"
          aria-label={`${t('visit.pharmacySelection.select')} ${pharmacy.name}`}
          loading={isPending}
        >
          {t('visit.pharmacySelection.select')}
        </Button>
      )}
    </div>
  );
}

function formatPharmacyLocation(pharmacy: Pharmacy) {
  if ('fullAddress' in pharmacy) {
    return pharmacy.fullAddress ?? '';
  }
  return `${pharmacy.address1}, ${pharmacy.address2 ? `${pharmacy.address2}, ` : ''}${pharmacy.city}, ${pharmacy.state} ${pharmacy.zip}`;
}
